import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../utils/supabase';
import Button from '../ui/Button';
import { 
  BarChart3, 
  Plus, 
  FileText, 
  MessageCircle, 
  Video, 
  Users, 
  Sparkles, 
  Building2 
} from 'lucide-react';

const EmployerHeaderNav = ({ title, subtitle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [messageCount, setMessageCount] = useState(0);
  const [interviewCount, setInterviewCount] = useState(0);
  const [applicationCount, setApplicationCount] = useState(0);

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      path: '/employer-dashboard',
      description: 'View applications and job performance'
    },
    {
      id: 'post-job',
      label: 'Post Job',
      icon: Plus,
      path: '/job-posting',
      description: 'Create new job postings'
    },
    {
      id: 'applications',
      label: 'Applications',
      icon: FileText,
      path: '/employer-dashboard?tab=applications',
      description: 'Review and manage applications',
      count: applicationCount
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: MessageCircle,
      path: '/messages',
      description: 'Conversations with candidates',
      count: messageCount
    },
    {
      id: 'interviews',
      label: 'Interviews',
      icon: Video,
      path: '/interview-management',
      description: 'Schedule and manage interviews',
      count: interviewCount
    },
    {
      id: 'candidates',
      label: 'Candidates',
      icon: Users,
      path: '/candidate-search',
      description: 'Browse and search candidates'
    },
    {
      id: 'ai-match',
      label: 'Auto Match',
      icon: Sparkles,
      path: '/auto-matching',
      description: 'Find candidates using AI',
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: Building2,
      path: '/employer-profile-settings',
      description: 'Manage company profile'
    }
  ];

  // Fetch initial counts
  const fetchInitialCounts = useCallback(async () => {
    if (!currentUser?.id) return;

    try {
      // Fetch unread message count - messages sent TO the employer
      const { count: msgCount, error: messageError } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', currentUser.id)
        .eq('is_read', false);

      if (!messageError) {
        setMessageCount(msgCount || 0);
      }

      // Fetch upcoming interview count - interviews created by this employer
      const now = new Date().toISOString();
      const { count: intCount, error: interviewError } = await supabase
        .from('interview_schedules')
        .select('*', { count: 'exact', head: true })
        .eq('interviewer_id', currentUser.id)
        .eq('status', 'scheduled')
        .gte('scheduled_at', now);

      if (!interviewError) {
        setInterviewCount(intCount || 0);
      }

      // Fetch new applications count for employer's jobs
      // First get all jobs for this employer
      const { data: employerJobs, error: jobsError } = await supabase
        .from('jobs')
        .select('id')
        .eq('employer_id', currentUser.id);

      if (!jobsError && employerJobs) {
        const jobIds = employerJobs.map(job => job.id);
        
        if (jobIds.length > 0) {
          // Count pending applications for these jobs
          const { count: appCount, error: applicationError } = await supabase
            .from('job_applications')
            .select('*', { count: 'exact', head: true })
            .in('job_id', jobIds)
            .eq('status', 'pending');

          if (!applicationError) {
            setApplicationCount(appCount || 0);
          }
        }
      }
    } catch (error) {
      console.error('[EmployerHeaderNav] Error fetching initial counts:', error);
    }
  }, [currentUser?.id]);

  // Set up realtime subscriptions
  useEffect(() => {
    if (!currentUser?.id) return;

    fetchInitialCounts();

    // Subscribe to message changes
    const messagesChannel = supabase
      .channel('employer_header_nav_messages')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'messages',
          filter: `recipient_id=eq.${currentUser.id}`
        }, 
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          if (eventType === 'INSERT' && !newRecord.is_read) {
            setMessageCount(prev => prev + 1);
          } else if (eventType === 'UPDATE') {
            if (oldRecord.is_read === false && newRecord.is_read === true) {
              setMessageCount(prev => Math.max(0, prev - 1));
            } else if (oldRecord.is_read === true && newRecord.is_read === false) {
              setMessageCount(prev => prev + 1);
            }
          } else if (eventType === 'DELETE' && !oldRecord.is_read) {
            setMessageCount(prev => Math.max(0, prev - 1));
          }
        }
      )
      .subscribe();

    // Subscribe to interview changes
    const interviewsChannel = supabase
      .channel('employer_header_nav_interviews')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'interview_schedules',
          filter: `employer_id=eq.${currentUser.id}`
        }, 
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          const now = new Date();
          
          if (eventType === 'INSERT') {
            if (newRecord.status === 'scheduled' && new Date(newRecord.scheduled_at) > now) {
              setInterviewCount(prev => prev + 1);
            }
          } else if (eventType === 'UPDATE') {
            const wasUpcoming = oldRecord.status === 'scheduled' && new Date(oldRecord.scheduled_at) > now;
            const isUpcoming = newRecord.status === 'scheduled' && new Date(newRecord.scheduled_at) > now;
            
            if (wasUpcoming && !isUpcoming) {
              setInterviewCount(prev => Math.max(0, prev - 1));
            } else if (!wasUpcoming && isUpcoming) {
              setInterviewCount(prev => prev + 1);
            }
          } else if (eventType === 'DELETE') {
            if (oldRecord.status === 'scheduled' && new Date(oldRecord.scheduled_at) > now) {
              setInterviewCount(prev => Math.max(0, prev - 1));
            }
          }
        }
      )
      .subscribe();

    // Subscribe to application changes for employer's jobs
    const applicationsChannel = supabase
      .channel('employer_header_nav_applications')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'job_applications'
        }, 
        async (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          // Verify the application is for one of employer's jobs
          const { data: job } = await supabase
            .from('jobs')
            .select('id')
            .eq('id', newRecord?.job_id || oldRecord?.job_id)
            .eq('employer_id', currentUser.id)
            .single();

          if (!job) return; // Not employer's job, ignore

          if (eventType === 'INSERT') {
            if (newRecord.status === 'pending') {
              setApplicationCount(prev => prev + 1);
            }
          } else if (eventType === 'UPDATE') {
            const wasPending = oldRecord.status === 'pending';
            const isPending = newRecord.status === 'pending';
            
            if (wasPending && !isPending) {
              setApplicationCount(prev => Math.max(0, prev - 1));
            } else if (!wasPending && isPending) {
              setApplicationCount(prev => prev + 1);
            }
          } else if (eventType === 'DELETE') {
            if (oldRecord.status === 'pending') {
              setApplicationCount(prev => Math.max(0, prev - 1));
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(interviewsChannel);
      supabase.removeChannel(applicationsChannel);
    };
  }, [currentUser?.id, fetchInitialCounts]);

  const isActivePath = (path) => {
    if (path.includes('?tab=')) {
      const [basePath, tabParam] = path.split('?tab=');
      const urlParams = new URLSearchParams(location.search);
      const activeTab = urlParams.get('tab');
      return location.pathname === basePath && activeTab === tabParam;
    }
    return location.pathname === path;
  };

  const handleNavigation = (path) => {
    if (path.includes('?tab=')) {
      const [basePath, tabParam] = path.split('?tab=');
      navigate(`${basePath}?tab=${tabParam}`);
    } else {
      navigate(path);
    }
  };

  // If no user is logged in, don't render the navigation header
  if (!currentUser) {
    return null;
  }

  return (
    <div className="bg-white shadow-sm border-b border-gray-200 sticky top-16 z-30">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        {/* Desktop Navigation - Full text labels (lg and above) */}
        <div className="hidden lg:flex justify-center space-x-1 overflow-x-auto">
          {navigationItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className={`relative flex items-center space-x-2 px-4 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                isActivePath(item.path)
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
              title={item.description}
            >
              <item.icon size={16} className="flex-shrink-0" />
              <span>{item.label}</span>
              {item.count > 0 && (
                <span className="absolute -top-0 -right-1 bg-red-500 text-white text-xs font-bold rounded-full mt-1 h-5 w-5 flex items-center justify-center animate-pulse">
                  {item.count > 99 ? '99+' : item.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tablet Navigation - Icon-only squeezed (md to lg) */}
        <div className="hidden md:flex lg:hidden justify-center items-center gap-4 space-x-1 overflow-x-auto py-2 px-2">
          {navigationItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className={`relative flex flex-col items-center justify-center px-2.5 py-1.5 rounded-lg transition-all flex-shrink-0 ${
                isActivePath(item.path)
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
              title={item.description}
            >
              <item.icon size={18} className="flex-shrink-0" />
              <span className="text-xs mt-0.5 text-center max-w-10 truncate">{item.label}</span>
              {item.count > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center animate-pulse text-0.5">
                  {item.count > 9 ? '9+' : item.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmployerHeaderNav;
